import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { cn } from "../../components/ui/cn";
import { Panel } from "../../components/ui/Panel";

/**
 * Which aliases each agent may send as, and which one they default to.
 *
 * An agent with nothing ticked can still reply on whichever alias a ticket
 * arrived at -- answering mail addressed to you is not choosing an identity.
 * The ticks govern the addresses they can *choose*.
 */
export function SendFromSettings() {
  const queryClient = useQueryClient();

  const grants = useQuery({
    queryKey: queryKeys.sendFromGrants,
    queryFn: api.sendFromGrants,
  });
  const channels = useQuery({
    queryKey: queryKeys.channels,
    queryFn: api.channels,
  });

  const save = useMutation({
    mutationFn: ({
      id,
      channelIds,
      defaultChannelId,
    }: {
      id: number;
      channelIds: number[];
      defaultChannelId: number | null;
    }) => api.setSendFromGrant(id, channelIds, defaultChannelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sendFromGrants });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sendFrom });
    },
  });

  return (
    <Panel
      collapsible
      title="Send-from addresses"
    >
      <div className="space-y-4">
        {(grants.data ?? []).map((agent) => (
          <div key={agent.id} className="rounded-md border border-line p-3">
            <div className="mb-2 flex items-baseline gap-2">
              <span className="text-sm font-medium text-content">
                {agent.name}
              </span>
              <span className="text-xs text-muted">{agent.email}</span>
            </div>

            <div className="space-y-1">
              {(channels.data ?? []).map((channel) => {
                const allowed = agent.channelIds.includes(channel.id);
                const isDefault = agent.defaultChannelId === channel.id;

                return (
                  <div key={channel.id} className="flex items-center gap-3">
                    <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-content">
                      <input
                        type="checkbox"
                        checked={allowed}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...agent.channelIds, channel.id]
                            : agent.channelIds.filter((id) => id !== channel.id);
                          save.mutate({
                            id: agent.id,
                            channelIds: next,
                            // Un-ticking the default clears it, rather than
                            // leaving a default they cannot send from.
                            defaultChannelId: next.includes(
                              agent.defaultChannelId ?? -1,
                            )
                              ? agent.defaultChannelId
                              : null,
                          });
                        }}
                        className="shrink-0 accent-brand-600"
                      />
                      {/* Truncated, so a long web form URL on a phone does
                          not wrap and push its Default out of the column. */}
                      <span className="min-w-0 truncate">{channel.address}</span>
                    </label>

                    <label
                      className={cn(
                        "flex shrink-0 items-center gap-1.5 text-xs",
                        allowed ? "text-muted" : "text-faint",
                      )}
                    >
                      <input
                        type="radio"
                        name={`default-${agent.id}`}
                        checked={isDefault}
                        disabled={!allowed}
                        onChange={() =>
                          save.mutate({
                            id: agent.id,
                            channelIds: agent.channelIds,
                            defaultChannelId: channel.id,
                          })
                        }
                        className="accent-brand-600"
                      />
                      Default
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
